import { useTranslation } from 'react-i18next'
import Alert from '@/shared/components/Alert/Alert'
import { Row } from '../../schema/document'
import { ElementType, FullWidthElementType } from '../../schema/elements'
import { canPlaceElement } from '../../utils/documentOps'
import styles from './ElementPalette.module.scss'

interface ElementPaletteProps {
  row: Row
  columnId: string
  onPick: (type: ElementType) => void
}

/**
 * What can go into a cell, in two groups.
 *
 * The split is the schema's own: five blocks own a whole row and the other
 * eight do not. Grouping them is what lets the rule be stated once, above the
 * group it applies to, instead of once inside every tile it blocks. Nothing is
 * hidden: a block that cannot go here is still drawn, dimmed and still legible,
 * because an option that vanishes teaches nothing.
 */
type PlainElementType = Exclude<ElementType, FullWidthElementType>

const PLAIN: { type: PlainElementType; icon: string }[] = [
  { type: 'heading', icon: 'las la-heading' },
  { type: 'text', icon: 'las la-align-left' },
  { type: 'image', icon: 'las la-image' },
  { type: 'button', icon: 'las la-hand-pointer' },
  { type: 'gallery', icon: 'las la-images' },
  { type: 'openingHours', icon: 'las la-clock' },
  { type: 'divider', icon: 'las la-minus' },
  { type: 'spacer', icon: 'las la-arrows-alt-v' }
]

const WIDE: { type: FullWidthElementType; icon: string }[] = [
  { type: 'menuFromPos', icon: 'las la-utensils' },
  { type: 'reservation', icon: 'las la-calendar-check' },
  { type: 'contactForm', icon: 'las la-envelope' },
  { type: 'map', icon: 'las la-map-marked-alt' },
  { type: 'video', icon: 'las la-video' }
]

function ElementPalette(props: ElementPaletteProps) {
  const { row, columnId, onPick } = props
  const { t } = useTranslation()

  /*
    Two questions, not thirteen: every block in a group is refused for the same
    reason as its neighbours or for none, because the rule is about the column,
    not about the block.
  */
  const plain = canPlaceElement(row, columnId, PLAIN[0].type)
  const placement = canPlaceElement(row, columnId, WIDE[0].type)

  return (
    <div className={styles.palette}>
      {!plain.allowed && (
        <Alert
          severity="info"
          message={t('WEBSITE.PALETTE.BLOCKED_BY_WIDE_NOTE')}
        />
      )}

      <div className={styles.grid}>
        {PLAIN.map(({ type, icon }) => (
          <button
            key={type}
            type="button"
            className={styles.option}
            disabled={!plain.allowed}
            onClick={() => onPick(type)}
          >
            <i className={`${icon} icon-big`} />
            <span className={`label-bold-medium ${styles.optionLabel}`}>
              {t(`WEBSITE.ELEMENT.${type}`)}
            </span>
          </button>
        ))}
      </div>

      <section className={styles.group}>
        <p className={`label-bold-medium ${styles.groupTitle}`}>
          {t('WEBSITE.PALETTE.WIDE_TITLE')}
        </p>

        {/*
          One message per dialog. When the column is already taken the note
          above covers everything in here too, so this group falls back to its
          plain description rather than repeating the same sentence.
        */}
        {placement.allowed || !plain.allowed ? (
          <p className={`body-small ${styles.groupHint}`}>
            {t('WEBSITE.PALETTE.WIDE_HINT')}
          </p>
        ) : (
          <Alert
            severity="info"
            message={t(
              placement.reason === 'layout'
                ? 'WEBSITE.PALETTE.BLOCKED_LAYOUT_NOTE'
                : 'WEBSITE.PALETTE.BLOCKED_OCCUPIED_NOTE'
            )}
          />
        )}

        <div className={styles.wideGrid}>
          {WIDE.map(({ type, icon }) => (
            <button
              key={type}
              type="button"
              className={styles.wideOption}
              disabled={!placement.allowed}
              onClick={() => onPick(type)}
            >
              <i className={`${icon} icon-medium`} />
              <span className={`label-bold-medium ${styles.optionLabel}`}>
                {t(`WEBSITE.ELEMENT.${type}`)}
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  )
}

export default ElementPalette
